import React from "react";
import { ShieldCheck, ShieldAlert, ShieldX, Trash2, RotateCcw, FileX2 } from "lucide-react";
import { generateFakeFilename } from "@/lib/fakeData";

/**
 * Quarantine Vault — lists tracks the user actively quarantined from the
 * LemonShield scan dialog. Empty state shows the classic "zero threats"
 * screen. Populated state shows a chunky P2P-style threat list with
 * Restore + Purge actions.
 */
export default function QuarantineView({ items = [], onRestore, onPurgeOne, onPurgeAll }) {
  const hasItems = items.length > 0;

  return (
    <div
      data-testid="quarantine-view"
      className="flex flex-col flex-1 min-h-0 bevel-in bg-white overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-[#ECE9D8] border-b border-[#808080]">
        <ShieldAlert size={14} className={hasItems ? "text-[#E81123]" : "text-[#FBC02D]"} />
        <span className="text-[12px] font-bold">Quarantine Vault &mdash; Isolated Threats</span>
        <span className={`ml-auto text-[11px] font-bold flex items-center gap-1 ${hasItems ? "text-[#8B0A14]" : "text-[#1F8020]"}`}>
          {hasItems ? <ShieldX size={12} /> : <ShieldCheck size={12} />}
          {items.length} item{items.length === 1 ? "" : "s"}
        </span>
      </div>

      {!hasItems ? <EmptyState /> : (
        <>
          {/* Action bar */}
          <div className="flex items-center gap-2 px-2 py-1.5 bg-[#ECE9D8] border-b border-[#9C9A7E] text-[11px]">
            <span className="text-[#8B0A14] font-bold">
              {items.length} threat{items.length === 1 ? "" : "s"} isolated
            </span>
            <span className="text-[#555]">·</span>
            <span className="text-[#555] italic">
              Restore at your own risk — or purge them into the void.
            </span>
            <button
              data-testid="quarantine-purge-all"
              onClick={onPurgeAll}
              className="ml-auto bevel-btn-red px-3 py-[3px] text-[11px] font-bold flex items-center gap-1"
            >
              <Trash2 size={10} /> Purge all
            </button>
          </div>

          {/* Threat list */}
          <div className="flex-1 overflow-y-auto xp-scroll">
            {items.map((it, i) => (
              <div
                key={`${it.track.id}-${it.quarantinedAt}`}
                data-testid={`quarantine-item-${i}`}
                className={`flex items-start gap-2 px-2 py-2 border-b border-[#F0EFE7] ${i % 2 === 0 ? "bg-[#FFF8F8]" : "bg-white"}`}
              >
                <div className="w-8 h-8 bevel-out bg-[#FFEBEE] flex items-center justify-center shrink-0">
                  <FileX2 size={16} className="text-[#8B0A14]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span
                      className="font-mono text-[#8B0A14] font-bold truncate"
                      title={generateFakeFilename(it.track)}
                      style={{ fontFamily: '"Share Tech Mono", monospace' }}
                    >
                      {generateFakeFilename(it.track)}
                    </span>
                    <span
                      className="ml-auto text-[9px] px-1.5 py-[1px] rounded-sm font-bold uppercase tracking-wider shrink-0"
                      style={{
                        background: it.status === "suspicious"
                          ? "linear-gradient(to bottom, #F58D85, #E81123, #8B0A14)"
                          : "linear-gradient(to bottom, #FFF59D, #FFEB3B, #FBC02D)",
                        color: it.status === "suspicious" ? "white" : "#3D2E00",
                        border: "1px solid rgba(0,0,0,0.3)",
                      }}
                    >
                      {it.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#222] mt-0.5 truncate">
                    {it.track.artist} &mdash; {it.track.title}
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {it.threats.map((t, idx) => (
                      <li
                        key={`${t}-${idx}`}
                        className="flex items-center gap-1.5 text-[11px] font-mono text-[#8B0A14]"
                      >
                        <ShieldAlert size={10} className="shrink-0 text-[#E81123]" />
                        <span className="truncate">{t}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-[10px] text-[#555] italic mt-1">
                    Quarantined {formatRelative(it.quarantinedAt)}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`quarantine-restore-${i}`}
                    onClick={() => onRestore?.(it)}
                    className="bevel-btn px-2 py-[3px] text-[10.5px] flex items-center gap-1 whitespace-nowrap"
                    title="Release this file from quarantine and add it back to your queue"
                  >
                    <RotateCcw size={10} /> Restore
                  </button>
                  <button
                    data-testid={`quarantine-purge-${i}`}
                    onClick={() => onPurgeOne?.(it)}
                    className="bevel-btn-red px-2 py-[3px] text-[10.5px] flex items-center gap-1 whitespace-nowrap"
                    title="Delete permanently"
                  >
                    <Trash2 size={10} /> Purge
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-2 py-1 text-[11px] bg-[#ECE9D8] border-t border-[#9C9A7E] text-[#555] flex justify-between">
            <span>LemonShield™ v4.2 GOLD · definitions updated Feb 18, 2026</span>
            <span>Quarantine is wiped every 30 days (but this is a demo, so, forever).</span>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
      <div
        className="w-24 h-24 flex items-center justify-center bevel-out"
        style={{
          background: "radial-gradient(circle, #B6FF00 0%, #32CD32 60%, #1F8020 100%)",
          borderRadius: "50%",
          boxShadow: "0 0 20px rgba(57,255,20,0.4), inset 0 0 20px rgba(0,0,0,0.2)",
        }}
      >
        <ShieldCheck size={56} className="text-white" style={{ filter: "drop-shadow(1px 1px 0 rgba(0,0,0,0.4))" }} />
      </div>

      <div>
        <div
          className="text-[24px] font-bold text-[#1F8020]"
          style={{ fontFamily: '"Pixelify Sans", Tahoma, sans-serif', textShadow: "1px 1px 0 #B6FF00" }}
        >
          ZERO THREATS DETECTED
        </div>
        <div className="text-[12px] text-[#222] italic mt-1">
          Your swarm is squeaky clean.
        </div>
      </div>

      <div className="bevel-in bg-[#F5FFF5] p-3 w-full max-w-md text-[11px] text-left space-y-1.5 font-mono">
        <InfoLine label="Last scan" value="2 seconds ago" />
        <InfoLine label="Total scans performed" value="1,482,029" />
        <InfoLine label="Threats caught all-time" value="0" />
        <InfoLine label="False positives" value="0" />
        <InfoLine label="LemonShield™ version" value="4.2 GOLD" />
        <InfoLine label="Definitions updated" value="Feb 18, 2026" />
      </div>

      <div className="text-[11px] text-[#555] italic max-w-md">
        Remember: LemonShield&trade; has never let a virus through. Not even the
        <span className="text-[#8B0A14] font-bold"> I-LOVE-YOU.vbs </span>
        one. Not even when your uncle emailed it to you.
      </div>

      <div className="text-[10px] text-[#777] flex items-center gap-1">
        <Trash2 size={10} /> Quarantined files appear here after you click "Quarantine" on the scan dialog.
      </div>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#555]">{label}</span>
      <span className="font-bold text-[#1F8020]">{value}</span>
    </div>
  );
}

function formatRelative(ts) {
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? "" : "s"} ago`;
}
